import type {
  ArrayField,
  Field,
  Form,
  FormState,
  ObjectField,
} from './magicalFormsTypes';

type AnyObjectField = ObjectField<Record<string, Field>>;
type AnyArrayField = ArrayField<Field>;
type TouchStateNode =
  | { touched?: boolean; [key: string]: unknown }
  | readonly unknown[];

function isObjectField(field: Field): field is AnyObjectField {
  return field.kind === 'object';
}

function isArrayField(field: Field): field is AnyArrayField {
  return field.kind === 'array';
}

function isTouchStateObject(
  state: TouchStateNode
): state is Exclude<TouchStateNode, readonly unknown[]> {
  return !Array.isArray(state);
}

function getTouchedState(field: Field, state: TouchStateNode): TouchStateNode {
  if (isObjectField(field)) {
    const objectState = state as Record<string, TouchStateNode>;
    const nextState: Record<string, unknown> = { ...objectState };

    for (const key in field.fields) {
      const childField = field.fields[key];
      const childState = objectState[key] ?? { touched: false };

      nextState[key] = getTouchedState(childField, childState);
    }

    return nextState;
  }

  if (isArrayField(field)) {
    const arrayState = Array.isArray(state) ? state : [];

    return arrayState.map((element) => {
      return getTouchedState(field.element, element as TouchStateNode);
    });
  }

  if (Array.isArray(state)) {
    return state;
  }

  if (isTouchStateObject(state) && state.touched) {
    return state;
  }

  return { ...state, touched: true };
}

/**
 * Touches every leaf field in a form so existing validation errors become visible.
 * This lives in @balance-web/forms because it intentionally traverses the forms
 * field/state tree shape, including `form._field`.
 */
export function touchAllFormFields<TField extends Field>(form: Form<TField>) {
  const nextState = getTouchedState(
    form._field,
    form.state as unknown as TouchStateNode
  ) as unknown as FormState<TField>;

  (form.setState as (state: FormState<TField>) => void)(nextState);
}
